import { NotifyError, isPhoneNumberValid } from "@ch/helpers/utils/helpers";
import useAddConsent from "@ch/helpers/hooks/useAddConsent";
import { useFormik } from "formik";
import type { FormEventHandler } from "react";
import * as Yup from "yup";

const useConsentForm = (onSuccess?: () => void) => {
  const { initiateAddConsent, loading } = useAddConsent();

  const {
    values,
    errors,
    touched,
    isValid,
    dirty,
    handleChange,
    setFieldTouched,
    setTouched,
    handleSubmit,
  } = useFormik({
    initialValues: {
      guardianName: "",
      phone: "",
      countryCode: "+234",
      email: "",
      agree: false,
    },
    validationSchema: Yup.object({
      guardianName: Yup.string().required("Name is required"),
      email: Yup.string()
        .email("Enter a valid email")
        .required("Email is required"),
      phone: Yup.string().when("countryCode", ([countryCode], schema) =>
        schema.test(
          "phone-valid",
          "Please enter a valid phone number",
          (value) => isPhoneNumberValid(value, countryCode as string),
        ),
      ),
      agree: Yup.boolean().oneOf([true], "You must agree to continue"),
    }),
    onSubmit: async (submitValues) => {
      await initiateAddConsent({
        requestBody: {
          guardianName: submitValues.guardianName,
          phone: submitValues.phone,
          countryCode: submitValues.countryCode,
          email: submitValues.email,
        },
        onSuccess,
      });
    },
  });

  const formFieldsProps = {
    guardianName: {
      onBlur: () => setFieldTouched("guardianName"),
      onChange: handleChange("guardianName"),
      value: values.guardianName,
      error: touched.guardianName ? errors.guardianName : "",
    },
    phone: {
      onBlur: () => setFieldTouched("phone"),
      onValueChange: handleChange("phone"),
      value: values.phone,
      error: touched.phone ? errors?.phone : "",
    },
    countryCode: {
      onBlur: () => setFieldTouched("countryCode"),
      onChange: handleChange("countryCode"),
      value: values.countryCode,
      error: touched.countryCode ? errors?.countryCode : "",
    },
    email: {
      onBlur: () => setFieldTouched("email"),
      onChange: handleChange("email"),
      value: values.email,
      error: touched.email ? errors?.email : "",
    },
    agree: {
      onChange: handleChange("agree"),
      checked: values.agree,
    },
  } as const;

  const canSubmit = isValid && dirty;

  const handleConsentSubmit: FormEventHandler = (event) => {
    event.preventDefault();
    if (!canSubmit) {
      void setTouched({
        guardianName: true,
        phone: true,
        email: true,
        agree: true,
      });
      NotifyError("Please fill in all required fields correctly.");
      return;
    }
    void handleSubmit();
  };

  return {
    formFieldsProps,
    handleConsentSubmit,
    canSubmit,
    loading,
  };
};

export default useConsentForm;
